/*
  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import makeStyles from "@mui/styles/makeStyles";
import useTheme from "@mui/styles/useTheme";
import React from "react";
import classnames from "classnames";

interface SpinnerProps {
  position?: string;
  className?: string;
  size?: "pico" | "nano" | "mini" | "small" | "medium" | "large";
  color?: "inherit" | "primary" | "secondary";
}

const useStyles = makeStyles(theme => ({
  root: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 1,
    visibility: "visible",

    // position
    "&.-absolute": {
      position: "absolute"
    },

    "&.-fixed": {
      position: "fixed"
    },

    "&.-relative": {
      position: "relative"
    }
  }
}));

export const Spinner = (props: SpinnerProps) => {
  const { position, className, size = "small", color = "inherit" } = props;
  const classes = useStyles();
  const theme = useTheme();
  const classNames = classnames("marapp-qa-spinner", classes.root, {
    [`-${position}`]: true,
    [className]: !!className,
    [size]: !!size
  });

  const SIZES = {
    pico: theme.spacing(1),
    nano: theme.spacing(2),
    mini: theme.spacing(3),
    small: theme.spacing(5),
    medium: theme.spacing(7),
    large: theme.spacing(10)
  };

  return (
    <Box
      className={classNames}
      display="flex"
      justifyContent="center"
      alignItems="center"
      p={1}
    >
      <CircularProgress size={SIZES[size]} color={color} />
    </Box>
  );
};
